/**
 * A paragraph and a picture from Wikipedia, so a thing on the list feels like
 * a thing rather than a row.
 *
 * Free, no key, and it serves `access-control-allow-origin: *`, so the browser
 * calls it directly and no token ever needs to exist. What it is *not* is
 * reliable at finding the right article, and that is the whole difficulty:
 *
 *   "Differently Morphous"      → 404 on the title, and a search returns
 *                                 *Yahtzee Croshaw*, the author
 *   "Shadow of the Leviathan"   → a search returns *Robert Jackson Bennett*
 *   "A Drop of Corruption"      → the actual novel
 *
 * Silently printing the author's biography under a book's title is precisely
 * the substitution `description_source` exists to prevent. So the match is
 * checked, and an article about the *person* is labelled as being about the
 * person — or dropped when it is neither.
 */

import type { WatchItem } from './model.js';

const API = 'https://en.wikipedia.org/w/api.php';
const SUMMARY = 'https://en.wikipedia.org/api/rest_v1/page/summary';

export interface WikiArticle {
  /** What the article is about, relative to the thing you asked for. */
  about: 'item' | 'creator';
  title: string;
  extract: string;
  url: string;
  thumbnail: string | null;
}

/**
 * Comparable form of a title: no case, no accents, no punctuation, and no
 * parenthetical qualifier — Wikipedia disambiguates with "(novel)" and
 * "(TV series)" and those must not count as a mismatch.
 */
export function normaliseTitle(value: string): string {
  return value
    .normalize('NFKD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/\s*\([^)]*\)\s*$/, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();
}

/**
 * Is this article about the thing, about its author, or about neither?
 *
 * The hard case is a title that *extends* another one. "Nevermoor: The Trials
 * of Morrigan Crow" is the same thing as "Nevermoor"; "Dune Messiah" is not
 * the same thing as "Dune". The difference is the colon — a subtitle explains
 * a title, whereas extra bare words make a different work — so an extension
 * only counts when it follows a colon or a dash.
 */
export function classifyArticle(articleTitle: string, item: WatchItem): WikiArticle['about'] | null {
  const article = normaliseTitle(articleTitle);
  const title = normaliseTitle(item.title);
  const articleMain = normaliseTitle(beforeSubtitle(articleTitle));
  const titleMain = normaliseTitle(beforeSubtitle(item.title));

  if (
    article === title ||
    articleMain === title ||
    article === titleMain ||
    (articleMain !== '' && articleMain === titleMain)
  ) {
    return 'item';
  }

  // A series article that names itself as one: "The Nevermoor Series" against
  // "Nevermoor".
  const articleBare = withoutNoise(articleMain);
  const titleBare = withoutNoise(titleMain);
  if (articleBare !== '' && articleBare === titleBare) return 'item';

  // The author or director. Real, and worth reading — but it is not the book,
  // and saying so is the entire point of this function.
  const byline = item.byline ? normaliseTitle(item.byline) : '';
  if (byline && article === byline) {
    return item.kind === 'creator' ? 'item' : 'creator';
  }

  return null;
}

/** Everything before a subtitle marker, which is where the qualifier lives. */
function beforeSubtitle(value: string): string {
  const cut = value.search(/[:—–]/);
  return cut === -1 ? value : value.slice(0, cut);
}

/**
 * "The Nevermoor Series" → "nevermoor". A leading article and a trailing
 * category word carry no identifying information, and Wikipedia adds both.
 */
function withoutNoise(value: string): string {
  return value
    .replace(/^the /, '')
    .replace(/\b(series|novels?|books?|trilogy|films?|franchise)\b/g, '')
    .replace(/\s+/g, ' ')
    .trim();
}

/** The search terms most likely to land on the thing itself. */
export function wikiQuery(item: WatchItem): string {
  const kind =
    item.kind === 'creator'
      ? ''
      : item.kind === 'series'
        ? item.medium === 'book'
          ? 'book series'
          : 'series'
        : item.medium === 'film'
          ? 'film'
          : item.medium === 'tv'
            ? 'tv series'
            : 'novel';
  return [item.title, item.byline ?? '', kind].filter(Boolean).join(' ');
}

interface SearchHit {
  title?: string;
}

/**
 * The best article for this item, or null when Wikipedia has nothing that is
 * plausibly about it. Prefers an article about the thing over one about its
 * author, whatever order the search returned them in.
 */
export async function findArticle(
  item: WatchItem,
  signal?: AbortSignal,
): Promise<WikiArticle | null> {
  const params = new URLSearchParams({
    action: 'query',
    list: 'search',
    srsearch: wikiQuery(item),
    srlimit: '5',
    format: 'json',
    // Wikipedia only sends the permissive CORS header when asked to.
    origin: '*',
  });

  const res = await fetch(`${API}?${params}`, { ...(signal ? { signal } : {}) });
  if (!res.ok) return null;

  const body = (await res.json()) as { query?: { search?: SearchHit[] } };
  const hits = (body.query?.search ?? [])
    .map((hit) => hit.title)
    .filter((t): t is string => Boolean(t));

  const classified = hits
    .map((title) => ({ title, about: classifyArticle(title, item) }))
    .filter((c): c is { title: string; about: WikiArticle['about'] } => c.about !== null);

  const best = classified.find((c) => c.about === 'item') ?? classified[0];
  if (!best) return null;

  return summarise(best.title, best.about, signal);
}

async function summarise(
  title: string,
  about: WikiArticle['about'],
  signal?: AbortSignal,
): Promise<WikiArticle | null> {
  const res = await fetch(`${SUMMARY}/${encodeURIComponent(title)}?redirect=true`, {
    ...(signal ? { signal } : {}),
  });
  if (!res.ok) return null;

  const body = (await res.json()) as {
    title?: string;
    extract?: string;
    type?: string;
    thumbnail?: { source?: string };
    content_urls?: { desktop?: { page?: string } };
  };

  // A disambiguation page is a list of links, not a description of anything.
  if (body.type === 'disambiguation' || !body.extract) return null;

  return {
    about,
    title: body.title ?? title,
    extract: body.extract,
    url:
      body.content_urls?.desktop?.page ??
      `https://en.wikipedia.org/wiki/${encodeURIComponent(title)}`,
    thumbnail: body.thumbnail?.source ?? null,
  };
}
